import { useCallback, useRef, useState } from "react";
import UploadsService from "../services/uploads.service";
import { getVideoDuration } from "../utils/getVideoDuration";
const PART_SIZE = 8 * 1024 * 1024;

export function useUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(null);

  const upload = useCallback(async ({ formData }) => {
    if (!formData?.thumbnail) throw new Error("Debes subir una imagen de portada.");
    if (!formData?.video) throw new Error("Debes subir un video.");

    setIsUploading(true);
    setProgress(0);
    abortRef.current = new AbortController();

    try {
      // 0) Duración ANTES de subir
      const durationInSeconds = await getVideoDuration(formData.video);
      console.log("Duración del video (segundos):", durationInSeconds);

      // normalizar profesionales
      let profesionalsIDs = [];
      if (Array.isArray(formData.profesionals)) {
        profesionalsIDs = formData.profesionals
          .map((v) => Number(v))
          .filter((v) => !Number.isNaN(v));
      } else if (formData.profesionals) {
        const n = Number(formData.profesionals);
        if (!Number.isNaN(n)) profesionalsIDs = [n];
      }
  
      const dataLive = {
        title: formData.title,
        categoryId: formData.category || null,
        workshopId: formData.workshop ? Number(formData.workshop) : null,
        dateLive: formData.date,
        profesionalsIDs,
        workshopNumber: formData.workshopNumber ?? null,
      };

      // 1) init
      const initRes = await UploadsService.init({
        fileNameImage: formData.thumbnail.name,
        fileTypeImage: formData.thumbnail.type || "image/jpeg",
        fileNameVideo: formData.video.name,
        fileTypeVideo: formData.video.type || "video/mp4",
        data: dataLive,
      });

      const { image, video } = initRes;

      console.log("image.headers", image.headers)
      console.log("image.url", image.url)
      console.log("formData.thumbnail", formData.thumbnail)
      console.log("abortRef.current.signal", abortRef.current.signal)

      // 2) PUT imagen
      const putRes = await fetch(image.url, {
        method: "PUT",
        headers: { ...(image.headers || {}) },
        body: formData.thumbnail,
        signal: abortRef.current.signal,
      });
      if (!putRes.ok) {
        const text = await putRes.text().catch(() => "(sin body)");
        throw new Error(
          `Error subiendo imagen: ${putRes.status} ${putRes.statusText} — ${text}`
        );
      }

      // 3) multipart video
      const file = formData.video;
      const parts = [];
      let uploadedBytes = 0;

      for (let partNumber = 1, start = 0; start < file.size; partNumber++) {
        const end = Math.min(start + PART_SIZE, file.size);
        const blob = file.slice(start, end);

        const url = await UploadsService.getPartUrl({
          key: video.keyNC,
          uploadId: video.multipart.uploadId,
          partNumber,
        });

        const res = await fetch(url, {
          method: "PUT",
          body: blob,
          signal: abortRef.current.signal,
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "(sin body)");
          throw new Error(
            `Fallo subiendo parte #${partNumber}: ${res.status} ${res.statusText} — ${text}`
          );
        }

        const etag = res.headers.get("ETag");
        if (!etag) throw new Error(`No se obtuvo ETag en la parte #${partNumber}`);
        parts.push({ ETag: etag.replaceAll('"', ""), PartNumber: partNumber });

        uploadedBytes += blob.size;
        setProgress(Math.round((uploadedBytes / file.size) * 100));
        start = end;
      }

      // 4) complete
      await UploadsService.completeMultipart({
        key: video.keyNC,
        uploadId: video.multipart.uploadId,
        parts,
      });

      setProgress(100);

      // keyNC: "sirenas/uploads/admin/lives/live-.../video/liv.mp4"
      const base = video.keyNC.split("/video")[0];
      const imageExt = formData.thumbnail.type.split("/")[1];

      // Key REAL de la imagen en S3 (sin dominio, sin query)
      const imageKey = new URL(image.url).pathname.replace(/^\/+/, '');
      // Ejemplo: "sirenas/uploads/admin/lives/live-.../thumbnail.jpg"


      return {
        ok: true,
        live: {
          acc: `${base}/audio/live.2.aac`,
          mp3: `${base}/audio/live.1.mp3`,
          thumbnail: imageKey,
          video: `${base}/video/live.mp4`,
          duration: Math.round(durationInSeconds),
          title: dataLive.title,
          workshopNumber: dataLive.workshopNumber,
          streamingDate: dataLive.dateLive,
          workshopId: dataLive.workshopId,
          categoryId: dataLive.categoryId,
          archive: false,
        },
      };
    } finally {
      setIsUploading(false);
      abortRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return { upload, isUploading, progress, cancel };
}
